import { HttpClient } from "@angular/common/http";
import { Injectable, InjectionToken, Inject } from "@angular/core";
import { Observable } from "rxjs";
import { OAuthStorageAdapter } from "@impacgroup/angular-oauth-base";
import { ApiListQueryParameter, BaseDatatableStateSaveMode } from "@impacgroup/angular-baselib";
import { map } from "rxjs/operators";
import { plainToClass } from "class-transformer";
import { DistributorImportOverview } from "../../api-models/DistributorImportOverview";
import { DistributorObj, DistributorStatus } from "src/app/api-models/Checklist";
import { DistributorDetails, GoogleMapsApiObject } from "src/app/api-models/Distributor";

export interface IDistributorConfigService {
    distributorsAPI: string;
    googleMapsAPI: string;
    dateFormat: string;
    utcDateFormat: string;
    datatableStateSaveMode: BaseDatatableStateSaveMode;
}

interface IDistributorStatePatchRequest {
    version: number;
    disabled: boolean;
}

export interface IDistributorGPSPatchRequest {
    _id: string;
    version: number;
    gpsLocation: {
        coordinates: number[];
    };
}

export type DistributorApiListQueryParams = ApiListQueryParameter & {
    isActive?: boolean;
    disabled?: boolean;
    missingCoords?: boolean;
    status?: DistributorStatus;
    fromPostCode?: string;
    toPostCode?: string;
    city?: string;
    salesline?: string;
    selectedLifecycleStatusFilter?: string;
    asmFilter?: string;
}

export const DistributorConfigService = new InjectionToken<IDistributorConfigService>("DistributorConfig");

@Injectable()
export class DistributorsService {
    public dateFormat: string;
    public UTCDATEFORMAT: string = "";
    public datatableStateSaveMode: BaseDatatableStateSaveMode;

    constructor(@Inject(DistributorConfigService) private distributorConfig: IDistributorConfigService, private http: HttpClient, private oauthStorageAdapter: OAuthStorageAdapter) {
        this.dateFormat = this.distributorConfig.dateFormat;
        this.UTCDATEFORMAT = this.distributorConfig.utcDateFormat;
        this.datatableStateSaveMode = this.distributorConfig.datatableStateSaveMode;
    }

    public list(params: DistributorApiListQueryParams): Observable<{ list: DistributorObj[]; count: number; total: number }> {
        let queryParams = params as any;

        if (params.isActive === true) {
            queryParams = {
                ...queryParams,
                isActive: true
            };
        }

        if (params.isActive === false) {
            queryParams = {
                ...queryParams,
                isActive: false
            };
        }

        if (params.disabled === true) {
            queryParams = {
                ...queryParams,
                disabled: true
            };
        }

        if (params.disabled === false) {
            queryParams = {
                ...queryParams,
                disabled: false
            };
        }

        if (params.missingCoords === true) {
            queryParams = {
                ...queryParams,
                missingCoords: true
            };
        }

        if (params.missingCoords === false) {
            queryParams = {
                ...queryParams,
                missingCoords: false
            };
        }

        if (params.status) {
            queryParams = {
                ...queryParams,
                status: params.status
            };
        }

        if (params.fromPostCode !== undefined) {
            queryParams = {
                ...queryParams,
                fromPostCode: params.fromPostCode
            };
        }

        if (params.toPostCode !== undefined) {
            queryParams = {
                ...queryParams,
                toPostCode: params.toPostCode
            };
        }

        if (params.city !== undefined) {
            queryParams = {
                ...queryParams,
                city: params.city
            };
        }

        if (params.salesline !== undefined) {
            queryParams = {
                ...queryParams,
                salesline: params.salesline
            };
        }

        if (params.selectedLifecycleStatusFilter !== undefined) {
            queryParams = {
                ...queryParams,
                lifecycleStatus: params.selectedLifecycleStatusFilter
            };
        }

        if (params.asmFilter !== undefined) {
            queryParams = {
                ...queryParams,
                asm: params.asmFilter
            };
        }

        Object.keys(queryParams).forEach(key => queryParams[key] === undefined && delete queryParams[key])

        return this.http
            .get<any>(this.getDistributorsServiceUrl(""), {
                headers: this.oauthStorageAdapter.getAuthHeadersJSON(),
                params: queryParams,
                observe: "response"
            })
            .pipe(
                map((result) => {
                    let count = result.body.length;
                    let total = result.body.length;
                    try {
                        count = parseInt(result.headers.get("X-Pagination-Count"), 10);
                    } catch (e) {}
                    try {
                        total = parseInt(result.headers.get("X-Total-Count"), 10);
                    } catch (e) {}

                    return {
                        list: plainToClass(DistributorObj, result.body as any[]),
                        count: count,
                        total: total
                    };
                })
            );
    }

    public getLifecycleStatusItems(): Observable<string[]> {
        return this.http.get<string[]>(this.getDistributorsServiceUrl("lifecyclestatus"), {
            headers: this.oauthStorageAdapter.getAuthHeadersJSON()
        });
    }

    public getDistributorImportOverview(): Observable<DistributorImportOverview> {
        return this.http
            .get<Object>(this.getDistributorsServiceUrl("import"), {
                headers: this.oauthStorageAdapter.getAuthHeadersJSON()
            })
            .pipe(map((result) => plainToClass(DistributorImportOverview, result)));
    }

    public uploadDistributorList(formData: FormData): Observable<DistributorImportOverview> {
        return this.http
            .post<void>(this.getDistributorsServiceUrl("import"), formData, {
                headers: this.oauthStorageAdapter.getAuthHeadersWithoutContentType()
            })
            .pipe(map((result) => plainToClass(DistributorImportOverview, result)));
    }

    public getAsmItems(): Observable<string[]> {
        return this.http.get<string[]>(this.getDistributorsServiceUrl("asmitems"), {
            headers: this.oauthStorageAdapter.getAuthHeadersJSON()
        });
    }

    private getDistributorsServiceUrl(postfix: string): string {
        return this.distributorConfig.distributorsAPI + postfix;
    }

    private getGoogleMapsServiceUrl(postfix: string): string {
        return this.distributorConfig.googleMapsAPI + postfix;
    }

    public changeDistributorState(distributor: DistributorObj): Observable<DistributorObj> {
        const request: IDistributorStatePatchRequest = {
            version: distributor.version,
            disabled: !distributor.disabled
        };

        return this.http
            .patch<Object>(this.getDistributorsServiceUrl(distributor._id), request, {
                headers: this.oauthStorageAdapter.getAuthHeadersJSON()
            })
            .pipe(map((result) => plainToClass(DistributorObj, result)));
    }

    public updateDistributorCoords(distributor: IDistributorGPSPatchRequest): Observable<DistributorObj> {
        return this.http
            .patch<Object>(this.getDistributorsServiceUrl(distributor._id), distributor, {
                headers: this.oauthStorageAdapter.getAuthHeadersJSON()
            })
            .pipe(map((result) => plainToClass(DistributorObj, result)));
    }

    public distributorDetails(id: string): Observable<DistributorDetails> {
        return this.http
            .get<Object>(this.getDistributorsServiceUrl(id), {
                headers: this.oauthStorageAdapter.getAuthHeadersJSON()
            })
            .pipe(map((result) => plainToClass(DistributorDetails, result)));
    }

    public getGoogleMapsKey(): Observable<GoogleMapsApiObject> {
        return this.http
            .get<Object>(this.getGoogleMapsServiceUrl(""), {
                headers: this.oauthStorageAdapter.getAuthHeadersJSON()
            })
            .pipe(map((result) => plainToClass(GoogleMapsApiObject, result)));
    }
}
